import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { ApiError, api } from '@/lib/api';
import type {
  AdminUser,
  ApiKeyExpiry,
  ApiKeyWithSecret,
  CreateApiKeyRequest,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 관리자 페이지에서는 owner select 노출. 사용자 페이지에서는 self 강제. */
  variant: 'admin' | 'user';
  onCreated: (key: ApiKeyWithSecret) => void;
}

interface FormState {
  name: string;
  expiryKind: 'never' | 'at';
  expiryAt: string;
  ownerUserId: string;
}

const EMPTY: FormState = { name: '', expiryKind: 'never', expiryAt: '', ownerUserId: '' };

export function ApiKeyFormModal({ open, onClose, variant, onCreated }: Props) {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="API 키 발급" size="md">
      <ApiKeyForm variant={variant} onClose={onClose} onCreated={onCreated} />
    </Modal>
  );
}

function ApiKeyForm({
  variant,
  onClose,
  onCreated,
}: {
  variant: 'admin' | 'user';
  onClose: () => void;
  onCreated: (key: ApiKeyWithSecret) => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.adminUsersList(),
    enabled: variant === 'admin',
    staleTime: 60_000,
  });

  const mut = useMutation({
    mutationFn: (body: CreateApiKeyRequest) =>
      variant === 'admin' ? api.adminApiKeyCreate(body) : api.apiKeyCreate(body),
    onSuccess: (key) => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      qc.invalidateQueries({ queryKey: ['admin', 'api-keys'] });
      setForm(EMPTY);
      setErrors({});
      setServerError(null);
      onCreated(key);
      onClose();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '발급에 실패했습니다.');
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = '이름은 필수입니다.';
    if (form.expiryKind === 'at') {
      if (!form.expiryAt) {
        next.expiryAt = '만료 시각을 선택하세요.';
      } else {
        const at = new Date(form.expiryAt);
        if (Number.isNaN(at.getTime())) next.expiryAt = '잘못된 시각 형식입니다.';
        else if (at.getTime() <= Date.now()) next.expiryAt = '현재 시각보다 이후여야 합니다.';
      }
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const expiry: ApiKeyExpiry =
      form.expiryKind === 'never'
        ? { kind: 'never' }
        : { kind: 'at', at: new Date(form.expiryAt).toISOString() };

    const body: CreateApiKeyRequest = {
      name: form.name.trim(),
      expiry,
    };
    if (variant === 'admin' && form.ownerUserId) body.ownerUserId = form.ownerUserId;
    mut.mutate(body);
  };

  const owners: AdminUser[] = usersQuery.data?.users ?? [];

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field
        label="이름"
        htmlFor="ak-name"
        hint="용도가 드러나는 라벨 (예: '내부 대시보드')."
        error={errors.name}
      >
        <input
          id="ak-name"
          value={form.name}
          maxLength={80}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
        />
      </Field>

      {variant === 'admin' ? (
        <Field
          label="소유 사용자"
          htmlFor="ak-owner"
          hint="비워두면 본인 계정에 발급. 다른 사용자에게 대리 발급할 수 있습니다."
        >
          <select
            id="ak-owner"
            value={form.ownerUserId}
            onChange={(e) => setForm((f) => ({ ...f, ownerUserId: e.target.value }))}
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
          >
            <option value="">본인</option>
            {owners.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName} ({u.email})
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      <Field label="만료" htmlFor="ak-expiry-kind" error={errors.expiryAt}>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="ak-expiry"
              checked={form.expiryKind === 'never'}
              onChange={() => setForm((f) => ({ ...f, expiryKind: 'never' }))}
            />
            <span>만료 없음 (영구)</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="ak-expiry"
              checked={form.expiryKind === 'at'}
              onChange={() => setForm((f) => ({ ...f, expiryKind: 'at' }))}
            />
            <span>지정 시각</span>
          </label>
          {form.expiryKind === 'at' ? (
            <input
              id="ak-expiry-at"
              type="datetime-local"
              value={form.expiryAt}
              onChange={(e) => setForm((f) => ({ ...f, expiryAt: e.target.value }))}
              className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
            />
          ) : null}
        </div>
      </Field>

      {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onClose} disabled={mut.isPending}>
          취소
        </Button>
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? '발급 중…' : '발급'}
        </Button>
      </div>
    </form>
  );
}
